import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { MainLayout } from '@/components/layout/MainLayout'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Card } from '@/components/ui/card'
import { ScrollArea } from '@/components/ui/scroll-area'
import { MessageSquare, Send, ArrowLeft, Inbox, Handshake } from 'lucide-react'
import { ThreadDeals } from '@adthlete/shared-frontend'
import { cn } from '@/lib/utils'
import {
  useGetDirectMessageThreadsQuery,
  useGetDirectThreadMessagesQuery,
  useSendDirectMessageReplyMutation,
  type DirectMessageThread,
} from '@/redux/marketerApi'
import { formatDistanceToNow } from 'date-fns'
import { ThreadOfferDialog } from '@/components/messages/ThreadOfferDialog'

// ─────────────────────────────────────────────
// Helper — relative time
// ─────────────────────────────────────────────
const timeAgo = (date: string) => {
  try {
    return formatDistanceToNow(new Date(date), { addSuffix: true })
  } catch {
    return date
  }
}

// ─────────────────────────────────────────────
// Thread List Item
// ─────────────────────────────────────────────
interface ThreadRowProps {
  name: string
  avatarSrc?: string | null
  subject?: string | null
  lastBody?: string
  isFromMe?: boolean
  unreadCount?: number
  lastMessageAt: string
  isActive: boolean
  onClick: () => void
}

const ThreadRow: React.FC<ThreadRowProps> = ({
  name,
  avatarSrc,
  subject,
  lastBody,
  isFromMe,
  unreadCount = 0,
  lastMessageAt,
  isActive,
  onClick,
}) => (
  <button
    onClick={onClick}
    className={cn(
      'w-full flex items-start gap-3 p-4 text-left transition-colors rounded-lg hover:bg-foreground/5',
      isActive && 'bg-muted',
    )}
  >
    <div className="relative shrink-0">
      <Avatar className="h-10 w-10">
        <AvatarImage src={avatarSrc ?? undefined} alt={name} />
        <AvatarFallback>{name.charAt(0)}</AvatarFallback>
      </Avatar>
      {unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-blue-500 border-2 border-foreground text-xs text-foreground flex items-center justify-center font-bold">
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between gap-2">
        <span className={cn('text-sm font-medium truncate', unreadCount > 0 && 'font-semibold')}>
          {name}
        </span>
        <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
          {timeAgo(lastMessageAt)}
        </span>
      </div>
      {subject && <p className="text-xs text-muted-foreground truncate mt-0.5">{subject}</p>}
      {lastBody && (
        <p
          className={cn(
            'text-xs truncate mt-0.5',
            unreadCount > 0 ? 'text-foreground font-medium' : 'text-muted-foreground',
          )}
        >
          {isFromMe ? 'You: ' : ''}
          {lastBody}
        </p>
      )}
    </div>
  </button>
)

// ─────────────────────────────────────────────
// Direct Message Thread Conversation
// ─────────────────────────────────────────────
interface ThreadConversationProps {
  threadId: string
  contactName: string
  contactAvatar?: string | null
}

const ThreadConversation: React.FC<ThreadConversationProps> = ({
  threadId,
  contactName,
  contactAvatar,
}) => {
  const [replyText, setReplyText] = useState('')
  const [offerOpen, setOfferOpen] = useState(false)
  const { data, isLoading, refetch } = useGetDirectThreadMessagesQuery(threadId)
  const athlete = data?.thread.contact
  const deals = data?.thread.deals ?? []
  const [sendReply, { isLoading: isSending }] = useSendDirectMessageReplyMutation()

  const handleSend = async () => {
    if (!replyText.trim() || isSending) return
    try {
      await sendReply({ threadId, message: replyText.trim() }).unwrap()
      setReplyText('')
    } catch (e) {
      console.error('Failed to send reply', e)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSend()
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-foreground/10 bg-card p-4 shrink-0">
        {/* Back button — visible on mobile, hidden on desktop */}
        <button
          onClick={() => window.history.back()}
          className="md:hidden shrink-0 -ml-1 mr-1 p-1 rounded-lg text-gray-600 hover:text-foreground transition-colors"
          aria-label="Back to inbox"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <Avatar className="h-9 w-9">
          <AvatarImage src={contactAvatar ?? undefined} alt={contactName} />
          <AvatarFallback>{contactName.charAt(0)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-sm text-foreground truncate">{contactName}</p>
          {data?.thread.subject && (
            <p className="text-xs text-gray-600 truncate">{data.thread.subject}</p>
          )}
        </div>
        {athlete && (
          <Button variant="lime" size="sm" className="shrink-0" onClick={() => setOfferOpen(true)}>
            <Handshake className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Book</span>
          </Button>
        )}
      </div>

      <ThreadDeals deals={deals} dealPath={(id) => `/collaborations/${id}`} />

      {athlete && (
        <ThreadOfferDialog
          open={offerOpen}
          onOpenChange={setOfferOpen}
          threadId={threadId}
          athlete={athlete}
          subtitle={data?.thread.talentSheet?.title}
          onSent={() => refetch()}
        />
      )}

      {/* Messages */}
      <ScrollArea className="flex-1 p-4">
        {isLoading && (
          <div className="flex justify-center py-8">
            <div className="animate-spin h-5 w-5 border-2 border-[#C0FD72] border-t-transparent rounded-full" />
          </div>
        )}
        <div className="space-y-3">
          {data?.messages.map((msg) => (
            <div
              key={msg.id}
              className={cn('flex gap-2', msg.isFromMe ? 'flex-row-reverse' : 'flex-row')}
            >
              {!msg.isFromMe && (
                <Avatar className="h-7 w-7 shrink-0 mt-1">
                  <AvatarImage src={contactAvatar ?? undefined} alt={contactName} />
                  <AvatarFallback className="text-xs">{contactName.charAt(0)}</AvatarFallback>
                </Avatar>
              )}
              <div
                className={cn(
                  'rounded-2xl px-4 py-2.5 max-w-[75%] text-sm leading-relaxed',
                  msg.isFromMe
                    ? 'bg-[#C0FD72] text-black rounded-br-sm'
                    : 'bg-muted text-gray-900 rounded-bl-sm border border-foreground/15',
                )}
              >
                {msg.byline && (
                  <p className="text-xs font-medium opacity-70 mb-0.5">{msg.byline}</p>
                )}
                <p>{msg.body}</p>
                <p className={cn('text-xs mt-1 opacity-60', msg.isFromMe ? 'text-right' : '')}>
                  {timeAgo(msg.createdAt)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>

      {/* Reply box */}
      <div className="border-t border-foreground/10 bg-card p-4 shrink-0">
        <div className="flex gap-2">
          <Textarea
            placeholder="Type a reply…"
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={2}
            className="resize-none flex-1 bg-card border-foreground/15 text-foreground placeholder:text-gray-500"
          />
          <Button
            onClick={handleSend}
            disabled={isSending || !replyText.trim()}
            size="icon"
            variant="lime"
            className="self-end h-9 w-9"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────
// Main Messages Page
// ─────────────────────────────────────────────
const Messages: React.FC = () => {
  const navigate = useNavigate()
  const { id: threadIdFromUrl } = useParams<{ id: string }>()

  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(threadIdFromUrl || null)
  const [selectedThread, setSelectedThread] = useState<DirectMessageThread | null>(null)
  const [search, setSearch] = useState('')

  const { data: dmData, isLoading: dmLoading } = useGetDirectMessageThreadsQuery()

  const threads = useMemo(() => dmData?.threads ?? [], [dmData?.threads])

  const filteredThreads = threads.filter(
    (t) =>
      t.contact.name.toLowerCase().includes(search.toLowerCase()) ||
      t.subject?.toLowerCase().includes(search.toLowerCase()),
  )

  // Sync URL changes to local state
  useEffect(() => {
    setSelectedThreadId(threadIdFromUrl || null)
  }, [threadIdFromUrl])

  // Sync selected thread object when ID changes
  useEffect(() => {
    if (selectedThreadId && threads.length > 0) {
      const thread = threads.find((t) => t.id === selectedThreadId)
      if (thread) {
        setSelectedThread(thread)
      }
    } else if (!selectedThreadId) {
      setSelectedThread(null)
    }
  }, [selectedThreadId, threads])

  const handleSelectThread = (thread: DirectMessageThread) => {
    navigate(`/messages/${thread.id}`)
  }

  // On mobile: show either the list OR the conversation (not both).
  // On desktop (md+): show side-by-side.
  const showList = !selectedThreadId
  const showConversation = !!selectedThreadId

  return (
    <MainLayout>
      <div className="flex h-[calc(100vh-4rem)] overflow-hidden">
        {/* ── Left panel: thread list ──────────────────── */}
        <div
          className={cn(
            'w-full md:w-80 md:min-w-[320px] md:max-w-[320px] border-r border-foreground/10 bg-card flex flex-col shrink-0 overflow-x-hidden',
            showList ? 'flex' : 'hidden md:flex',
          )}
        >
          {/* Header */}
          <div className="p-4 border-b border-foreground/10 flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-foreground" />
            <h1 className="text-3xl font-bold text-foreground">Messages</h1>
          </div>

          {/* Search */}
          <div className="px-3 py-2">
            <Input
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 text-sm bg-card border-foreground/15 text-foreground placeholder:text-gray-500"
            />
          </div>

          {/* Direct messages list */}
          <div className="flex-1 overflow-y-auto px-2">
            {dmLoading && (
              <div className="flex justify-center py-8">
                <div className="animate-spin h-5 w-5 border-2 border-[#C0FD72] border-t-transparent rounded-full" />
              </div>
            )}
            {!dmLoading && filteredThreads.length === 0 && (
              <div className="text-center py-12 text-gray-600">
                <Inbox className="h-10 w-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No direct messages yet</p>
                <Button
                  variant="link"
                  size="sm"
                  className="mt-1 text-[#3F7A0A] hover:text-[#3F7A0A]"
                  onClick={() => navigate('/marketplace')}
                >
                  Contact an athlete →
                </Button>
              </div>
            )}
            {filteredThreads.map((thread) => (
              <ThreadRow
                key={thread.id}
                name={thread.contact.name}
                avatarSrc={thread.contact.avatar}
                subject={thread.subject}
                lastBody={thread.lastMessage?.body}
                isFromMe={thread.lastMessage?.isFromMe}
                unreadCount={thread.unreadCount}
                lastMessageAt={thread.lastMessageAt}
                isActive={selectedThreadId === thread.id}
                onClick={() => handleSelectThread(thread)}
              />
            ))}
          </div>
        </div>

        {/* ── Right panel: conversation ─────────────────── */}
        <main
          className={cn(
            'flex-1 flex flex-col overflow-hidden min-w-0',
            showConversation ? 'flex' : 'hidden md:flex',
          )}
        >
          {selectedThreadId && selectedThread ? (
            <ThreadConversation
              threadId={selectedThreadId}
              contactName={selectedThread.contact.name}
              contactAvatar={selectedThread.contact.avatar}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center text-gray-600 gap-3">
              <MessageSquare className="h-16 w-16 opacity-20" />
              <p className="text-lg font-medium opacity-70 text-gray-700">Select a conversation</p>
              <p className="text-sm opacity-60 max-w-xs">
                Choose a conversation on the left, or find an athlete in the Marketplace.
              </p>
              <Button variant="dark-outline" size="sm" onClick={() => navigate('/marketplace')}>
                Go to the Marketplace
              </Button>
            </div>
          )}
        </main>
      </div>
    </MainLayout>
  )
}

export default Messages
